import { useState } from "react";

import { AppDialog, useAppDialog } from "app/utils";
import { CheckboxRow, Column, DialogWrapper, Text } from "@bridgesplit/ui";
import { formatPercent } from "@bridgesplit/utils";

import { AppButton } from "./auth";
import { DialogHeader } from "./dialog";

export const BORROW_APY_ABOVE_AVG_THRESHOLD = 0.5;

interface ApyStatusProps {
    netApyPct: number | undefined;
    borrowApy: number | undefined;
    avgApy: number | undefined;
    negativeNetApy: boolean;
    borrowApyAboveAvg: boolean;
}

function ApyStatus({ netApyPct, borrowApy, avgApy, negativeNetApy, borrowApyAboveAvg }: ApyStatusProps) {
    return (
        <Column spacing={1}>
            {negativeNetApy && (
                <Text color="warning" variant="body2">
                    Net loop APY is negative ({formatPercent(netApyPct)}). The collateral yield does not cover the
                    borrow cost. This position will lose value over time at current rates.
                </Text>
            )}
            {borrowApyAboveAvg && (
                <Text color="warning" variant="body2">
                    Borrow APY ({formatPercent(borrowApy)}) is more than{" "}
                    {formatPercent(BORROW_APY_ABOVE_AVG_THRESHOLD, { customDigits: 0 })} above the average (
                    {formatPercent(avgApy)}).
                </Text>
            )}
        </Column>
    );
}

export function BorrowRiskConfirmDialog() {
    const { getData, close } = useAppDialog();
    const data = getData(AppDialog.BorrowRiskConfirm);

    const [acceptedWarning, setAcceptedWarning] = useState(false);

    if (!data) return null;

    const { netApyPct, borrowApy, avgApy, negativeNetApy, borrowApyAboveAvg, onConfirm } = data;

    return (
        <DialogWrapper>
            <DialogHeader header="Confirm position risk" description="Review the risks before continuing." />

            <ApyStatus
                netApyPct={netApyPct}
                borrowApy={borrowApy}
                avgApy={avgApy}
                negativeNetApy={negativeNetApy}
                borrowApyAboveAvg={borrowApyAboveAvg}
            />

            <CheckboxRow
                label="I understand the risks and want to proceed"
                value={acceptedWarning}
                setValue={() => setAcceptedWarning((prev) => !prev)}
            />

            <AppButton
                isTransaction={false}
                disabled={!acceptedWarning}
                color="error"
                variant="outlined"
                asyncCta={{
                    onClickWithResult: async () => {
                        const result = await onConfirm();
                        if (result.isOk()) {
                            close();
                        }
                        return result;
                    },
                    options: { alertOnError: true }
                }}
            >
                Continue
            </AppButton>
        </DialogWrapper>
    );
}
